'use client';

import { API_BASE_URL } from '@/lib/api/base-url';
import { getClientBuildHeaders, getClientVersionDiagnostics, getVersionState } from '@/lib/version/client-version';
import { useAuthStore } from '@/stores/authStore';
import { getResizeObserverDiagnostics } from '@/lib/resize-observer-diagnostics';
import { getDomMutationDiagnostics } from '@/lib/dom-mutation-diagnostics';

const REPORT_TIMEOUT_MS = 3500;
const AUTO_RECOVERY_KEY = 'dobi-error-auto-recovery';
const CHUNK_ERROR_MARKERS = [
  'ChunkLoadError',
  'Loading chunk',
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
  'error loading dynamically imported module',
];

export interface ApiRequestDiagnostic {
  requestId?: string;
  endpoint?: string;
  status?: number;
  at: number;
}

export interface ClientErrorReportInput {
  recoveryId?: string;
  error?: Error;
  componentStack?: string | null;
  context?: 'app' | 'workspace';
  extra?: Record<string, unknown>;
}

export interface ClientErrorReportResult {
  recoveryId: string;
  fingerprint: string;
}

let lastApiDiagnostic: ApiRequestDiagnostic | null = null;
const reportsByError = new WeakMap<Error, {
  input: ClientErrorReportInput;
  promise: Promise<ClientErrorReportResult>;
}>();

/** Allowlisted diagnostics only: never copy URLs with query strings or DOM text. */
export function getClientErrorDiagnostics(error?: Error): Record<string, unknown> {
  if (!error) return {};
  if (error.message === 'Failed to load client build manifest') {
    const detail = (error as Error & {dobiManifestRecovery?: {state?: unknown; attempts?: unknown; round?: unknown}}).dobiManifestRecovery;
    return {errorKind: 'pages_build_manifest',
      manifestRecovery: detail?.state === 'exhausted' && (detail.attempts === 1 || detail.attempts === 2)
        ? {state: 'exhausted', attempts: detail.attempts,
            ...('round' in detail && Number.isSafeInteger(detail.round) && Number(detail.round) >= 1 && Number(detail.round) <= 1_000_000
              ? {round: detail.round} : {})} : {state: 'unobserved'}};
  }
  const detail = error as Error & {type?: unknown; request?: unknown; dobiChunkRecovery?: unknown};
  if (isChunkOrModuleLoadError(error)) {
    const diagnostics: Record<string, unknown> = {errorKind: 'chunk_load'};
    if (typeof detail.type === 'string' && /^(error|load|missing|timeout)$/.test(detail.type)) {
      diagnostics.chunkErrorType = detail.type;
    }
    if (typeof detail.request === 'string') {
      try {
        const request = new URL(detail.request, typeof window === 'undefined' ? 'https://localhost' : window.location.origin);
        if (/^\/_next\/static\/.+\.(?:js|css)$/.test(request.pathname)) {
          diagnostics.chunkRequestPath = request.pathname.slice(0, 512);
          diagnostics.chunkRequestOrigin = request.origin;
          diagnostics.chunkRequestRetried = request.searchParams.has('__dobi_chunk_retry');
        }
      } catch { /* A malformed diagnostic cannot prevent reporting the original error. */ }
    }
    const recovery = detail.dobiChunkRecovery;
    if (recovery && typeof recovery === 'object') {
      const record = recovery as Record<string, unknown>;
      if ((record.state === 'exhausted' || record.state === 'recovered')
        && typeof record.chunkId === 'string' && /^[\w-]{1,64}$/.test(record.chunkId)
        && typeof record.attempts === 'number' && Number.isInteger(record.attempts) && record.attempts >= 1 && record.attempts <= 3) {
        diagnostics.chunkRecovery = {state: record.state, chunkId: record.chunkId,
          attempts: record.attempts, sawTimeout: record.sawTimeout === true,
          elapsedMs: typeof record.elapsedMs === 'number' && Number.isFinite(record.elapsedMs)
            ? Math.max(0, Math.min(record.elapsedMs, 600_000)) : null};
      }
      if (typeof record.online === 'boolean') diagnostics.chunkOnline = record.online;
      if (record.resourceTiming && typeof record.resourceTiming === 'object') {
        const timing = record.resourceTiming as Record<string, unknown>;
        const selected: Record<string, number> = {};
        for (const key of ['duration', 'transferSize', 'encodedBodySize', 'decodedBodySize', 'responseStatus']) {
          const value = timing[key];
          if (typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1_000_000_000) selected[key] = value;
        }
        if (Object.keys(selected).length) diagnostics.chunkResourceTiming = selected;
      }
    }
    return diagnostics;
  }
  if (/Minified React error #418\b|Hydration failed because/i.test(error.message)) {
    return {errorKind: 'react_hydration', hydrationMismatch: /args(?:\[\]|%5B%5D)=text|server rendered text/i.test(error.message)
      ? 'text' : /args(?:\[\]|%5B%5D)=HTML|server rendered HTML/i.test(error.message) ? 'html' : 'unknown',
      hydrationRecoveryOutcome: 'unobserved'};
  }
  return getResizeObserverDiagnostics(error);
}

export function recordApiRequestDiagnostic(diagnostic: Omit<ApiRequestDiagnostic, 'at'>) {
  lastApiDiagnostic = {
    ...diagnostic,
    at: Date.now(),
  };
}

export function createRecoveryId(): string {
  const random = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID().replace(/-/g, '').slice(0, 12)
    : Math.random().toString(36).slice(2, 14);
  return `cerr-${Date.now().toString(36)}-${random}`;
}

export function getCurrentRoute(): string {
  if (typeof window === 'undefined') return '/';
  return `${window.location.pathname || '/'}${window.location.search || ''}`;
}

export function getProjectIdFromRoute(route = getCurrentRoute()): string | null {
  const match = route.match(/^\/p\/([^/?#]+)/);
  return match?.[1] ?? null;
}

export function isChunkOrModuleLoadError(error?: Error | null): boolean {
  if (!error) return false;
  const haystack = `${error.name || ''}\n${error.message || ''}\n${error.stack || ''}`;
  return CHUNK_ERROR_MARKERS.some(marker => haystack.includes(marker));
}

export function computeClientErrorFingerprint(
  error?: Error | null,
  componentStack?: string | null,
  route = getCurrentRoute(),
): string {
  const version = getVersionState();
  const source = [
    version.currentBuildId || '',
    route.split('?')[0] || '',
    error?.name || '',
    error?.message || '',
    (error?.stack || '').split('\n').slice(0, 4).join('\n'),
    (componentStack || '').split('\n').slice(0, 4).join('\n'),
  ].join('\n');

  let hash = 5381;
  for (let i = 0; i < source.length; i += 1) {
    hash = ((hash << 5) + hash) ^ source.charCodeAt(i);
  }
  return `fe-${(hash >>> 0).toString(16)}`;
}

export function consumeAutomaticRecoveryAttempt(kind: string, fingerprint: string): boolean {
  if (typeof window === 'undefined') return false;
  const version = getVersionState();
  const route = getCurrentRoute().split('?')[0];
  const key = `${kind}:${version.currentBuildId || 'unknown'}:${route}:${fingerprint}`;
  try {
    const raw = window.sessionStorage.getItem(AUTO_RECOVERY_KEY);
    const seen = raw ? JSON.parse(raw) as Record<string, number> : {};
    if (seen[key]) return false;
    seen[key] = Date.now();
    window.sessionStorage.setItem(AUTO_RECOVERY_KEY, JSON.stringify(seen));
    return true;
  } catch {
    return false;
  }
}

export function clearAutomaticRecoveryAttempts(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(AUTO_RECOVERY_KEY);
  } catch {
    // Ignore storage failures; recovery should stay best-effort.
  }
}

export async function clearRuntimeCaches(): Promise<void> {
  if (typeof window === 'undefined' || !('caches' in window)) return;
  const names = await caches.keys();
  await Promise.all(names.map(name => caches.delete(name)));
}

export function reportClientError(input: ClientErrorReportInput): Promise<ClientErrorReportResult> {
  const existing = input.error && reportsByError.get(input.error);
  if (existing) {
    // A global error can arrive immediately before React supplies its component
    // stack. Merge that same-turn evidence into one report, not a second alert.
    if (input.componentStack) existing.input.componentStack = input.componentStack;
    if (input.context) existing.input.context = input.context;
    existing.input.extra = {...existing.input.extra, ...input.extra};
    return existing.promise;
  }
  const queued = {...input, extra: {...input.extra}};
  const report = Promise.resolve().then(() => sendClientError(queued));
  if (input.error) reportsByError.set(input.error, {input: queued, promise: report});
  return report;
}

async function sendClientError(input: ClientErrorReportInput): Promise<ClientErrorReportResult> {
  const recoveryId = input.recoveryId || createRecoveryId();
  const route = getCurrentRoute();
  const version = getVersionState();
  const fingerprint = computeClientErrorFingerprint(input.error, input.componentStack, route);

  if (typeof window === 'undefined') {
    return { recoveryId, fingerprint };
  }

  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), REPORT_TIMEOUT_MS);

  try {
    const token = useAuthStore.getState().accessToken;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Request-ID': createRecoveryId().replace(/^cerr-/, 'cerrreq-').slice(0, 32),
      ...getClientBuildHeaders(),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const payload = JSON.stringify({
      recoveryId,
      fingerprint,
      buildId: version.currentBuildId,
      latestBuildId: version.latestBuildId,
      clientStale: version.isStale,
      route,
      projectId: getProjectIdFromRoute(route),
      errorName: input.error?.name,
      message: input.error?.message,
      stack: input.error?.stack,
      componentStack: input.componentStack,
      userAgent: navigator.userAgent,
      clientTimestamp: new Date().toISOString(),
      lastApiRequestId: lastApiDiagnostic?.requestId,
      lastApiStatus: lastApiDiagnostic?.status,
      lastApiEndpoint: lastApiDiagnostic?.endpoint,
      context: {
        boundaryContext: input.context,
        ...input.extra,
        ...(typeof getClientVersionDiagnostics === 'function' ? getClientVersionDiagnostics() : {}),
        ...getClientErrorDiagnostics(input.error),
        ...getDomMutationDiagnostics(input.error),
      },
    });

    const response = await fetch(`${API_BASE_URL}/client-errors`, {
      method: 'POST',
      headers,
      signal: controller.signal,
      keepalive: payload.length < 60000,
      body: payload,
    });

    const data = await response.json().catch(() => null) as { success?: boolean; data?: { recoveryId?: string } } | null;
    if (!response.ok || data?.success !== true) {
      throw new Error(`Client error reporting rejected: HTTP ${response.status}`);
    }
    return {
      recoveryId: data?.data?.recoveryId || recoveryId,
      fingerprint,
    };
  } catch (error) {
    console.warn('[ClientErrors] Failed to report client error:', error);
    return { recoveryId, fingerprint };
  } finally {
    window.clearTimeout(timer);
  }
}
